import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  registerShortcutListener,
  ShortcutAction,
  KEYBOARD_SHORTCUTS,
} from '@/lib/keyboard-shortcuts';

interface UseKeyboardShortcutsOptions {
  isAuthenticated: boolean;
  onShowHelp?: () => void;
  onLogout?: () => void;
}

/**
 * Custom hook for keyboard shortcuts
 */
export const useKeyboardShortcuts = (options: UseKeyboardShortcutsOptions) => {
  const navigate = useNavigate();
  const { isAuthenticated, onShowHelp, onLogout } = options;

  useEffect(() => {
    const handleShortcut = (action: ShortcutAction) => {
      switch (action) {
        // Navigation
        case 'navigate_dashboard':
          navigate('/');
          break;

        case 'navigate_search':
          navigate('/global-search');
          toast.success('Opened Global Search');
          break;

        case 'navigate_analytics':
          navigate('/analytics');
          toast.success('Opened Analytics');
          break;

        case 'navigate_customers':
          navigate('/pelanggan');
          toast.success('Opened Customers');
          break;

        case 'navigate_projects':
          navigate('/nilai-kos-projek');
          toast.success('Opened Projects');
          break;

        case 'navigate_invoices':
          navigate('/invoice');
          toast.success('Opened Invoices');
          break;

        case 'navigate_monitor':
          navigate('/system-monitor');
          toast.success('Opened System Monitor');
          break;

        // Search
        case 'open_search':
          navigate('/global-search');
          setTimeout(() => {
            const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
            if (searchInput) {
              searchInput.focus();
            }
          }, 100);
          break;

        case 'focus_search':
          const searchBox = document.querySelector('input[placeholder*="Cari"]') as HTMLInputElement;
          if (searchBox) {
            searchBox.focus();
          } else {
            toast.info('Search box not found on this page');
          }
          break;

        // Actions
        case 'toggle_sidebar':
          // Dispatch custom event that DashboardLayout can listen to
          window.dispatchEvent(new CustomEvent('toggle-sidebar'));
          break;

        case 'refresh_page':
          window.location.reload();
          break;

        case 'logout':
          if (onLogout) {
            onLogout();
          } else {
            localStorage.removeItem('auth_state');
            navigate('/login');
            toast.success('Logged out successfully');
          }
          break;

        // General
        case 'open_help':
          if (onShowHelp) {
            onShowHelp();
          }
          break;

        case 'toggle_theme':
          // Dispatch custom event that ThemeProvider can listen to
          window.dispatchEvent(new CustomEvent('toggle-theme'));
          toast.success('Theme toggled');
          break;

        default:
          break;
      }
    };

    // Register the keyboard shortcut listener
    const cleanup = registerShortcutListener(handleShortcut, isAuthenticated);

    // Cleanup on unmount
    return cleanup;
  }, [navigate, isAuthenticated, onShowHelp, onLogout]);
};

/**
 * Get keyboard shortcut description for tooltips
 */
export const getShortcutTooltip = (action: ShortcutAction): string => {
  const shortcut = KEYBOARD_SHORTCUTS[action];
  if (!shortcut) return '';

  const keys = shortcut.keys
    .map((key) => {
      if (key === 'Control') return 'Ctrl';
      if (key === 'Shift') return 'Shift';
      if (key === 'Alt') return 'Alt';
      return key.length === 1 ? key.toUpperCase() : key;
    })
    .join(' + ');

  return `${shortcut.description} (${keys})`;
};
